import {useFormik} from 'formik'
import {lowerCase, startCase, isEmpty, isUndefined} from 'lodash'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {Link, Navigate, useNavigate, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import {PasswordMeterComponent} from '../../../../_gori/assets/ts/components'
import {blockPopUp} from '../../../../_gori/helpers'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {Button, InputTextFormik} from '../../../../_gori/partials/widgets'
import {AcceptTermCheckbox} from '../../../../_gori/partials/widgets/form/AcceptTermCheckbox'
import {InputPassword} from '../../../../_gori/partials/widgets/form/InputPassword'
import AuthService from '../core/_requests'
import {VerifyManagerTokenResponse} from '../core/_models'
import ReCAPTCHA from 'react-google-recaptcha'
import {ROUTES} from '../../../../_gori/constants'

const KEY_RECAPTCHA = process.env.REACT_APP_KEY_RECAPTCHA

type AcceptManagerProps = {
  token?: string | null
}

export function AcceptManager({token: tokenProp}: AcceptManagerProps = {}) {
  const intl = useIntl()
  const navigate = useNavigate()
  const {newCancelToken, isCancel} = useCancelToken()
  const {authYup, regexYup} = UseYupValidate()
  const [searchParams] = useSearchParams()
  const token = tokenProp ?? searchParams.get('token')

  const [verifying, setVerifying] = useState(true)
  const [managerInvite, setManagerInvite] = useState<VerifyManagerTokenResponse | null>(null)
  const [expired, setExpired] = useState(false)
  const [expiredOrganizationName, setExpiredOrganizationName] = useState('')

  const [submitting, setSubmitting] = useState(false)
  const [isValidRecaptcha, setIsValidRecaptcha] = useState<boolean | undefined>(undefined)
  const [tokenRecaptcha, setTokenRecaptcha] = useState(null)
  const recaptchaRef = useRef(null)
  const [recaptchaError, setRecaptchaError] = useState({required: false, error: false})
  const [configCaptCha, setConfigCaptCha] = useState<boolean>(false)
  const verifiedBaseInformationRef = useRef(false)

  const registrationSchema = Yup.object().shape({
    first_name: regexYup.inputText('FIRST_NAME', true, 50),
    last_name: regexYup.inputText('LAST_NAME', true, 50),
    password: authYup.password(),
    password_confirmation: authYup.passwordConfirmation(),
    accept_terms: authYup.acceptTerms,
    receive_promotional_emails: authYup.receivePromotionalEmails,
  })

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      password: '',
      password_confirmation: '',
      accept_terms: false,
      receive_promotional_emails: false,
    },
    validationSchema: registrationSchema,
    onSubmit: async (values, {setSubmitting: setFormikSubmitting}) => {
      if (!token || !managerInvite) return
      if (!verifiedBaseInformationRef.current) {
        setFormikSubmitting(false)
        return
      }
      if (configCaptCha) {
        // @ts-ignore
        if (recaptchaRef.current.getValue() === null || recaptchaRef.current.getValue() === '') {
          setRecaptchaError({required: true, error: false})
          setIsValidRecaptcha(false)
          return
        }
      }

      try {
        setSubmitting(true)
        const payload: any = {
          company_name: managerInvite.company_name,
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
          password_confirmation: values.password_confirmation,
          accept_terms: values.accept_terms,
          receive_promotional_emails: values.receive_promotional_emails,
          token,
          account_ids: managerInvite.account_ids,
          ...(configCaptCha && tokenRecaptcha ? {token_recaptcha: tokenRecaptcha} : {}),
        }
        await AuthService.activeManager(payload, {cancelToken: newCancelToken()})

        const userId = managerInvite.email ?? token
        if ((window as any).posthog?.identify) {
          ;(window as any).posthog.identify(userId)
        }
        navigate(ROUTES.AUTH.LOGIN)
      } catch (error: any) {
        if (isCancel(error)) return

        if (configCaptCha) {
          if (error?.response?.data?.error?.fields?.invalid_recaptcha) {
            setRecaptchaError({required: false, error: true})
          }
          setIsValidRecaptcha(false)
          // @ts-ignore
          recaptchaRef.current.reset()
        }
        handleSubmitError(error?.response?.data?.error?.fields)
      } finally {
        setSubmitting(false)
        setFormikSubmitting(false)
      }
    },
  })

  const handleSubmitError = (errorsMessage: any) => {
    if (isEmpty(errorsMessage) || isUndefined(errorsMessage)) {
      return
    }

    Object.entries(errorsMessage).forEach(([key, value]: [any, any]) => {
      for (let i = 0; i < value.length; i++) {
        const errMessage = value[i].split('|')
        const message = intl.formatMessage(
          {id: errMessage[1]},
          {input: startCase(lowerCase(errMessage[0]))}
        )
        const existingError = formik.errors[key]

        formik.errors[key] = existingError ? `${existingError}, ${message}` : message
      }
    })
  }

  const verifyToken = useCallback(async () => {
    if (!token) {
      setVerifying(false)
      return
    }

    try {
      setVerifying(true)
      const response = (await AuthService.verifyManagerToken(
        {token},
        {cancelToken: newCancelToken()}
      )) as VerifyManagerTokenResponse

      if (response.expired) {
        setExpired(true)
        setExpiredOrganizationName(response.company_name ?? '')
        setManagerInvite(null)
        return
      }

      setManagerInvite(response)
    } catch (error: any) {
      if (isCancel(error)) return
      if (error?.response?.data?.error?.fields?.expired) {
        setExpired(true)
        setExpiredOrganizationName(error?.response?.data?.error?.fields?.company_name ?? '')
      }
      setManagerInvite(null)
    } finally {
      setVerifying(false)
    }
  }, [token, newCancelToken, isCancel])

  useEffect(() => {
    verifyToken()
  }, [verifyToken])

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  const getVersion = useCallback(async () => {
    try {
      await AuthService.getVersion()
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    getVersion()
  }, [getVersion])

  const getConfigCaptcha = useCallback(async () => {
    try {
      const response = await AuthService.getConfigCaptcha()
      setConfigCaptCha(response.data.config)
    } catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    getConfigCaptcha()
  }, [getConfigCaptcha])

  const handleRecaptchaChange = (tokenValue: React.SetStateAction<null>) => {
    if (tokenValue !== null) {
      setRecaptchaError({required: false, error: false})
      setTokenRecaptcha(tokenValue)
      setIsValidRecaptcha(true)
    }
  }

  const handleRecaptchaError = () => {
    // @ts-ignore
    recaptchaRef.current.reset()
    setIsValidRecaptcha(false)
  }

  const handleRecaptchaExpired = () => {
    setIsValidRecaptcha(false)
    // @ts-ignore
    recaptchaRef.current.reset()
  }

  const validateAndOpenVerifyModal = async () => {
    if (!token || !managerInvite) return

    const errors = await formik.validateForm()

    if (Object.keys(errors).length === 0) {
      try {
        setSubmitting(true)
        const payload: any = {
          company_name: managerInvite.company_name,
          first_name: formik.values.first_name,
          last_name: formik.values.last_name,
          password: formik.values.password,
          password_confirmation: formik.values.password_confirmation,
          accept_terms: formik.values.accept_terms,
          receive_promotional_emails: formik.values.receive_promotional_emails,
          token,
          account_ids: managerInvite.account_ids,
          ...(configCaptCha && tokenRecaptcha ? {token_recaptcha: tokenRecaptcha} : {}),
        }
        await AuthService.verify(payload, {cancelToken: newCancelToken()})

        verifiedBaseInformationRef.current = true
        formik.submitForm()
      } catch (error: any) {
        handleSubmitError(error?.response?.data?.error?.fields)
        verifiedBaseInformationRef.current = false
      }
      setSubmitting(false)
    } else {
      formik.setTouched(Object.keys(errors).reduce((acc, key) => ({...acc, [key]: true}), {}))
    }
  }

  if (!verifying && managerInvite && token && !managerInvite.is_new_user) {
    return <Navigate to={`${ROUTES.AUTH.LOGIN}?token=${encodeURIComponent(token)}`} replace />
  }

  return (
    <>
      {managerInvite && (
        <div
          className='d-flex justify-content-center text-center invite-message fw-bold fs-4'
          dangerouslySetInnerHTML={{
            __html: intl.formatMessage(
              {id: 'MANAGER_INVITATION_MESSAGE'},
              {organization_name: `<span class="fw-bolder">${managerInvite.company_name}</span>`}
            ),
          }}
        />
      )}
      <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
        {verifying ? (
          <div className='w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
            <div className='text-center py-10'>
              <div className='spinner-border spinner-custom border-5' role='status' />
            </div>
          </div>
        ) : expired ? (
          <div className='w-lg-800px bg-body rounded shadow-sm p-10 mx-auto'>
            <div className='text-center'>
              <div
                className='expired-token-message'
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage(
                    {id: 'MANAGER_INVITATION_EXPIRED'},
                    {
                      organization_name: `<span class="fw-bolder">${expiredOrganizationName}</span>`,
                    }
                  ),
                }}
              />
            </div>
          </div>
        ) : managerInvite && token ? (
          <div className='w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
            <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
              <div className='mb-10 text-center'>
                <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'CREATE_AN_ACCOUNT'})}</h1>
                <div className='text-gray-400 fw-bold fs-4'>
                  {intl.formatMessage({id: 'ALREADY_HAVE_AN_ACCOUNT'})}{' '}
                  <Link
                    to={ROUTES.AUTH.LOGIN}
                    className='text-primary-deep text-hover-primary fw-bolder'
                  >
                    {intl.formatMessage({id: 'SIGN_IN_HERE'})}
                  </Link>
                </div>
              </div>
              <div className='row fv-row mb-9 fv-plugins-icon-container'>
                <div className='col-xl-6'>
                  <InputTextFormik
                    labelClassName='form-label fw-bolder text-dark fs-6'
                    label={intl.formatMessage({id: 'FIRST_NAME'})}
                    size='lg'
                    formik={formik}
                    name='first_name'
                    required
                  />
                </div>
                <div className='col-xl-6'>
                  <InputTextFormik
                    labelClassName='form-label fw-bolder text-dark fs-6'
                    label={intl.formatMessage({id: 'LAST_NAME'})}
                    size='lg'
                    formik={formik}
                    name='last_name'
                    required
                  />
                </div>
              </div>
              <InputPassword
                className={'mb-10'}
                labelClassName='form-label fw-bolder text-dark fs-6'
                label={intl.formatMessage({id: 'PASSWORD'})}
                size={'lg'}
                strengthMessage={intl.formatMessage({id: 'PASSWORD_STRENGTH_MESSAGE'})}
                formik={formik}
                name={'password'}
                required
              />
              <InputPassword
                className={'mb-5'}
                labelClassName='form-label fw-bolder text-dark fs-6'
                label={intl.formatMessage({id: 'CONFIRM_PASSWORD'})}
                size='lg'
                openHighlight={false}
                formik={formik}
                name={'password_confirmation'}
                required
              />
              <AcceptTermCheckbox
                alignTop={true}
                className={'mb-5'}
                formik={formik}
                name={'accept_terms'}
                splitLabelCheck={[
                  intl.formatMessage({id: 'AGREE_MESSAGE_PRE_PHONE_VERIFY_1'}),
                  intl.formatMessage({id: 'AGREE_MESSAGE_PRE_PHONE_VERIFY_2'}),
                ]}
                links={[
                  {
                    label: intl.formatMessage({id: 'TERMS_OF_SERVICE'}),
                    onClick: () => {
                      if (blockPopUp('https://shipbae.com/service-terms')) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    },
                  },
                  {
                    label: intl.formatMessage({id: 'PRIVACY_POLICY'}),
                    onClick: () => {
                      if (blockPopUp('https://shipbae.com/privacy-policy')) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    },
                  },
                  {
                    label: intl.formatMessage({
                      id: 'USPS_LABEL_INTEGRITY_AND_COMPLIANCE_AGREEMENT',
                    }),
                    onClick: () => {
                      if (blockPopUp('https://shipbae.com/agreement')) {
                        toast.warning(intl.formatMessage({id: 'POP_UP_IS_BLOCKED'}))
                      }
                    },
                  },
                ]}
              />
              <div
                className={`fv-row fv-plugins-icon-container fv-plugins-bootstrap5-row-invalid mb-10`}
              >
                <div
                  className={`form-check form-check-custom form-check-solid form-check-inline align-items-start`}
                >
                  <input
                    type='checkbox'
                    className={`form-check-input shadow-none mt-1`}
                    {...formik.getFieldProps('receive_promotional_emails')}
                  />
                  <span className={`form-check-label fw-bold text-gray-700 fs-7`}>
                    {intl.formatMessage({id: 'AGREE_TO_RECEIVE_EMAILS'})}
                  </span>
                </div>
              </div>
              {configCaptCha && (
                <div className='d-flex justify-content-center mb-5'>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    onErrored={handleRecaptchaError}
                    sitekey={KEY_RECAPTCHA}
                    hl='en'
                    isolated={true}
                  />
                </div>
              )}
              {configCaptCha && recaptchaError.error && (
                <p className='d-flex justify-content-center text-danger'>
                  {intl.formatMessage({id: 'RECAPTCHA_ERROR_MESSAGE'})}
                </p>
              )}
              {configCaptCha && recaptchaError.required && (
                <p className='d-flex justify-content-center text-danger'>
                  {intl.formatMessage({id: 'RECAPTCHA_ERROR_REQUIRED'})}
                </p>
              )}
              <div className='text-center'>
                <Button
                  className='btn btn-lg btn-advanced-primary'
                  label={intl.formatMessage({id: 'SUBMIT'})}
                  loadingText={intl.formatMessage({id: 'SUBMIT'})}
                  loading={submitting}
                  disabled={
                    formik.isSubmitting ||
                    !formik.isValid ||
                    (isUndefined(isValidRecaptcha) ? undefined : !isValidRecaptcha)
                  }
                  event={validateAndOpenVerifyModal}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className='w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
            <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
              <div className='mb-10 text-center'>
                <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'CREATE_AN_ACCOUNT'})}</h1>
              </div>
              <div className='mb-10 alert alert-danger alert-dismissible'>
                <div className='alert-text'>
                  {intl.formatMessage({id: 'INVALID_MANAGER_INVITATION_LINK'})}
                </div>
              </div>
              <div className='d-flex flex-wrap justify-content-center gap-4 pb-lg-0'>
                <Link to={ROUTES.AUTH.LOGIN} className='link-primary fw-bolder'>
                  <button type='button' className='btn btn-lg btn-advanced-primary'>
                    {intl.formatMessage({id: 'LOGIN'})}
                  </button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </CSSTransition>
    </>
  )
}
